import {
  PreferredCreatorCity,
  PreferredCreatorCountry,
  PreferredCreatorState,
} from '@/types/campaign';
import type {LocationValue} from '@/types/models/creator-crm/creatorCrm';
import locationFetchers, {FetchedLocation} from '@/api/fetchers/location';

export type PreferredLocationsInput = {
  cities?: PreferredCreatorCity[];
  states?: PreferredCreatorState[];
  countries?: PreferredCreatorCountry[];
};

type PreferredLocation =
  | PreferredCreatorCity
  | PreferredCreatorState
  | PreferredCreatorCountry;

const LOCATION_KIND = {
  CITY: 'city',
  STATE: 'state',
  COUNTRY: 'country',
} as const;

type LocationKind = (typeof LOCATION_KIND)[keyof typeof LOCATION_KIND];

const matchString = (expected?: string, actual?: string) =>
  !expected || expected.toLowerCase() === actual?.toLowerCase();

function findMatchingLocation(
  fetchedLocations: Awaited<ReturnType<typeof locationFetchers.locations>>,
  location: PreferredLocation,
  kind: LocationKind
) {
  const locationName = location.name;
  const locationCountry = 'country' in location ? location.country : undefined;
  const locationState = 'state' in location ? location.state : undefined;

  const matchers: Record<LocationKind, (fetched: FetchedLocation) => boolean> =
    {
      [LOCATION_KIND.CITY]: (fetched) =>
        matchString(locationName, fetched.value.place) &&
        matchString(locationState, fetched.value.region) &&
        matchString(locationCountry, fetched.value.country),
      [LOCATION_KIND.STATE]: (fetched) =>
        matchString(locationName, fetched.value.region) &&
        matchString(locationCountry, fetched.value.country),
      [LOCATION_KIND.COUNTRY]: (fetched) =>
        matchString(locationName, fetched.value.country),
    };

  return fetchedLocations.find((fetched) => matchers[kind](fetched));
}

async function fetchAndMatchLocationValue(
  location: PreferredLocation,
  kind: LocationKind
): Promise<LocationValue | null> {
  const locationName = location.name as string;
  const fetchedLocations = await locationFetchers.locations(locationName);
  const matchedLocation =
    findMatchingLocation(fetchedLocations, location, kind) ||
    fetchedLocations[0];

  if (!matchedLocation) return null;

  return matchedLocation.value as LocationValue;
}

/**
 * Resolves campaign preferred locations via Mapbox and returns SegmentFilters-style
 * LocationValue[]. Used by campaignToSegmentFilters for the segment path only.
 */
export async function campaignPreferredLocationsToLocationValues(
  locations: PreferredLocationsInput
): Promise<LocationValue[]> {
  const {cities = [], states = [], countries = []} = locations;

  if (cities.length === 0 && states.length === 0 && countries.length === 0) {
    return [];
  }

  const cityPromises = cities.map((city) =>
    fetchAndMatchLocationValue(city as PreferredLocation, LOCATION_KIND.CITY)
  );

  const statePromises = states.map((state) =>
    fetchAndMatchLocationValue(state, LOCATION_KIND.STATE)
  );

  const countryPromises = countries.map((country) =>
    fetchAndMatchLocationValue(country, LOCATION_KIND.COUNTRY)
  );

  const results = await Promise.all([
    ...cityPromises,
    ...statePromises,
    ...countryPromises,
  ]);

  return results.filter((result): result is LocationValue => result !== null);
}
